import {
  AfterViewInit,
  ChangeDetectionStrategy,
  EventEmitter, Inject,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {BehaviorSubject, Observable} from 'rxjs';
import {CHECK_LAYOUT_DIRECTION_PROVIDER} from "../../providers";

@Component({
  selector: 'int-input',
  templateUrl: 'int-input.component.html',
  styleUrls: ['int-input-vars.component.scss', 'int-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: IntInputComponent,
      multi: true,
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IntInputComponent
  implements ControlValueAccessor, OnInit, AfterViewInit
{
  value$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  field$ = new BehaviorSubject<ElementRef>({} as ElementRef);

  dir$ = new BehaviorSubject('ltr');

  @Input()
  set value(value) {
    this.value$.next(value);
  }

  @Input()
  set direction(value: string) {
    if(value) {
      this.dir$.next(value);
    }else{
      this.dir$.next(this.directionValue.value)
    }
  }

  get value(): string {
    return this.value$.value;
  }

  @Input()
  placeholder = '';

  @Input()
  label = '';

  @Input()
  type = 'text';

  @Input() defaultHeight = '50px';
  @Input() defaultHeightPriority = false;

  @Input()
  showLabelTip = true;

  @Input()
  readonly = false;

  @Input()
  autocomplete: string | null = null;

  @Input()
  hideField: boolean = false;

  @Output()
  changeValueEvent: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  onBlurEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  onFocusEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

  disabled = false;
  touched = false;
  focused = false;

  @ViewChild('rootElement', { static: false })
  private readonly rootElement!: ElementRef;

  @ViewChild('field', { static: false })
  private readonly field!: ElementRef;

  @ViewChild('colswrap', { static: true })
  private readonly colswrap!: ElementRef;

  constructor(
    private readonly renderer2: Renderer2,
    @Inject(CHECK_LAYOUT_DIRECTION_PROVIDER)
    private readonly directionValue: BehaviorSubject<string>
  ) {}

  ngAfterViewInit() {
    const { height } = this.field.nativeElement.getBoundingClientRect();
    this.renderer2.setStyle(
      this.colswrap?.nativeElement,
      'height',
      this.defaultHeightPriority ? this.defaultHeight : height ? `${height}px`: this.defaultHeight
    );
    this.field$.next(this.field);
  }

  ngOnInit() {
    this.dir$.next(this.directionValue.value)
  }

  get currentLabel(): string {
    return this.label && (this.value || this.focused)
      ? this.label
      : this.placeholder || this.label;
  }

  get isNotLabelOrPlaceholder(): boolean {
    return !(Boolean(this.label) || Boolean(this.placeholder));
  }

  writeValue(value: string) {
    this.value$.next(value);
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  changeValue(event: Event) {
    const target = event.target as HTMLInputElement;
    this.value$.next(target.value);
    this.onChange(target.value);
    this.changeValueEvent.emit(this.value);
  }

  onChange(val: any) {}

  onTouched() {}

  setBlur() {
    this.field?.nativeElement.blur();
  }

  setFocus() {
    this.field?.nativeElement.focus();
  }

  onFocus() {
    this.focused = true;
    this.onFocusEvent.emit(true);
    this.onBlurEvent.emit(false);
  }
  onBlur() {
    this.focused = false;
    this.onFocusEvent.emit(false);
    this.onBlurEvent.emit(true);
  }
}
